import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

import {
    TemplateComponentButtonType,
    TemplateHeaderFormat,
    WhatsAppCarouselCard,
    WhatsAppQuickReplyButton,
    WhatsAppUrlButton,
} from 'domains/content/types';

import EmojiPicker from '../EmojiPicker';
import MergeTagPicker from '../MergeTagPicker';

const BODY_MAX_CHARS = 160;
const BUTTON_TEXT_MAX = 25;

type CarouselButton = WhatsAppQuickReplyButton | WhatsAppUrlButton;

const DEFAULT_CAROUSEL_BUTTON: Record<string, () => CarouselButton> = {
    [TemplateComponentButtonType.QUICK_REPLY]: () => ({ type: TemplateComponentButtonType.QUICK_REPLY, text: '' }),
    [TemplateComponentButtonType.URL]: () => ({ type: TemplateComponentButtonType.URL, text: '', url: '' }),
};

export type Props = {
    card: WhatsAppCarouselCard;
    index: number;
    canRemove: boolean;
    // All cards share the same button configuration — changes here apply to all
    sharedButtonConfig: CarouselButton[];
    onCardChange: (index: number, card: WhatsAppCarouselCard) => void;
    onSharedButtonConfigChange: (buttons: CarouselButton[]) => void;
    onRemove: (index: number) => void;
};

const CarouselCard = ({
    card,
    index,
    canRemove,
    sharedButtonConfig,
    onCardChange,
    onSharedButtonConfigChange,
    onRemove,
}: Props) => {
    const handleHeaderFormatChange = (e: SelectChangeEvent) => {
        onCardChange(index, {
            ...card,
            header_format: e.target.value as TemplateHeaderFormat.IMAGE | TemplateHeaderFormat.VIDEO,
        });
    };

    const handleHeaderUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onCardChange(index, { ...card, header_url: e.target.value });
    };

    const handleBodyChange = (value: string) => {
        onCardChange(index, { ...card, body_text: value.slice(0, BODY_MAX_CHARS) });
    };

    const handleInsert = (value: string) => {
        onCardChange(index, { ...card, body_text: (card.body_text + value).slice(0, BODY_MAX_CHARS) });
    };

    const handleAddButton = (e: SelectChangeEvent) => {
        if (sharedButtonConfig.length >= 2) return;
        onSharedButtonConfigChange([...sharedButtonConfig, DEFAULT_CAROUSEL_BUTTON[e.target.value]()]);
    };

    const handleButtonTextChange = (btnIndex: number, text: string) => {
        const updated = sharedButtonConfig.map((b, i) =>
            i === btnIndex ? { ...b, text: text.slice(0, BUTTON_TEXT_MAX) } : b
        ) as CarouselButton[];
        onSharedButtonConfigChange(updated);
    };

    const handleButtonUrlChange = (btnIndex: number, url: string) => {
        const updated = sharedButtonConfig.map((b, i) => (i === btnIndex ? { ...b, url } : b)) as CarouselButton[];
        onSharedButtonConfigChange(updated);
    };

    const handleRemoveButton = (btnIndex: number) => {
        onSharedButtonConfigChange(sharedButtonConfig.filter((_, i) => i !== btnIndex));
    };

    return (
        <Stack
            spacing={2}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}
            data-testid={`carousel-card-${index}`}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Card {index + 1}
                </Typography>
                {canRemove && (
                    <Tooltip title="Remove card">
                        <IconButton
                            size="small"
                            aria-label="Remove card"
                            data-testid={`remove-card-${index}`}
                            onClick={() => onRemove(index)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* Header */}
            <Stack spacing={1}>
                <Select
                    data-testid={`card-header-format-${index}`}
                    value={card.header_format}
                    onChange={handleHeaderFormatChange}
                    size="small"
                    fullWidth
                    inputProps={{ 'aria-label': `Card ${index + 1} header format` }}
                >
                    <MenuItem value={TemplateHeaderFormat.IMAGE}>Image</MenuItem>
                    <MenuItem value={TemplateHeaderFormat.VIDEO}>Video</MenuItem>
                </Select>
                <TextField
                    data-testid={`card-header-url-${index}`}
                    label={`${card.header_format === TemplateHeaderFormat.VIDEO ? 'Video' : 'Image'} URL`}
                    value={card.header_url ?? ''}
                    onChange={handleHeaderUrlChange}
                    size="small"
                    fullWidth
                    inputProps={{ 'aria-label': `Card ${index + 1} header URL` }}
                />
            </Stack>

            {/* Body */}
            <Stack spacing={0.5}>
                <TextField
                    data-testid={`card-body-${index}`}
                    label="Card body"
                    value={card.body_text}
                    onChange={(e) => handleBodyChange(e.target.value)}
                    multiline
                    rows={3}
                    size="small"
                    fullWidth
                    helperText={`${card.body_text.length} / ${BODY_MAX_CHARS}`}
                    inputProps={{ maxLength: BODY_MAX_CHARS, 'aria-label': `Card ${index + 1} body` }}
                />
                {index === 0 && (
                    <Stack direction="row">
                        <EmojiPicker handleSelect={handleInsert} />
                        <MergeTagPicker handleSelect={handleInsert} />
                    </Stack>
                )}
            </Stack>

            {/* Shared buttons (only edit on first card; shown read-only on others) */}
            {index === 0 && (
                <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                        Buttons · shared across all cards (max 2)
                    </Typography>
                    {sharedButtonConfig.map((btn, btnIndex) => (
                        <Stack
                            key={btnIndex}
                            spacing={1}
                            sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}
                        >
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">
                                    {btn.type === TemplateComponentButtonType.URL ? 'URL button' : 'Quick Reply button'}
                                </Typography>
                                <Tooltip title="Remove button">
                                    <IconButton
                                        size="small"
                                        aria-label="Remove carousel button"
                                        data-testid={`remove-carousel-button-${btnIndex}`}
                                        onClick={() => handleRemoveButton(btnIndex)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <TextField
                                data-testid={`carousel-button-text-${btnIndex}`}
                                label="Button text"
                                value={btn.text}
                                onChange={(e) => handleButtonTextChange(btnIndex, e.target.value)}
                                size="small"
                                fullWidth
                                inputProps={{ maxLength: BUTTON_TEXT_MAX, 'aria-label': 'Carousel button text' }}
                                helperText={`${btn.text.length} / ${BUTTON_TEXT_MAX}`}
                            />
                            {btn.type === TemplateComponentButtonType.URL && (
                                <TextField
                                    data-testid={`carousel-button-url-${btnIndex}`}
                                    label="URL"
                                    value={(btn as WhatsAppUrlButton).url}
                                    onChange={(e) => handleButtonUrlChange(btnIndex, e.target.value)}
                                    size="small"
                                    fullWidth
                                    inputProps={{ 'aria-label': 'Carousel button URL' }}
                                />
                            )}
                        </Stack>
                    ))}
                    {sharedButtonConfig.length < 2 && (
                        <Box>
                            <Select
                                data-testid="add-carousel-button-select"
                                value=""
                                onChange={handleAddButton}
                                displayEmpty
                                size="small"
                                sx={{ minWidth: 180 }}
                                inputProps={{ 'aria-label': 'Add carousel button' }}
                                renderValue={() => (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <AddIcon fontSize="small" />
                                        <span>Add button</span>
                                    </Stack>
                                )}
                            >
                                <MenuItem value={TemplateComponentButtonType.QUICK_REPLY}>Quick Reply</MenuItem>
                                <MenuItem value={TemplateComponentButtonType.URL}>URL</MenuItem>
                            </Select>
                        </Box>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default CarouselCard;
